import { type ReactNode, useEffect, useState } from 'react';

import { IconButton } from '@zen/DesignSystem';
import { downloadS3File } from '@zen/utils';
import { useNotification } from '@zen/utils/hooks/useNotification';
import usePreviewUrl from '@zen/utils/hooks/usePreviewUrl';

import { getFileExtension } from '../Documents/helpers';
import ZoomElement from '../ZoomElement';
import { CsvViewer, DocxViewer, PdfViewer, PhotoViewer, TextViewer, XlsxViewer } from './drivers';
import Unsupported from './drivers/Unsupported';

interface Props {
  errorComponent?: ReactNode;
  fileUrl: string;
  onError?: () => void;
  unsupportedComponent?: ReactNode;
}

const FileViewer: React.FC<Props> = ({ fileUrl }) => {
  const { addError } = useNotification();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const previewUrl: string = usePreviewUrl(fileUrl);
  const fileType: string = getFileExtension(fileUrl);

  const enableScrollY: boolean = !!fileType.match(/(txt|pdf|doc|docx)$/i);

  useEffect(() => {
    const container = document.getElementById('pg-viewer');

    if (container) {
      setDimensions({
        width: container.clientWidth,
        height: container.clientHeight
      });
    }
  }, []);

  const getDriver = () => {
    switch (fileType) {
      case 'csv':
        return CsvViewer;
      case 'docx':
        return DocxViewer;
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'bmp':
      case 'png':
        return PhotoViewer;
      case 'pdf':
        return PdfViewer;
      case 'txt':
        return TextViewer;
      case 'xlsx':
        return XlsxViewer;
      default:
        return Unsupported;
    }
  };

  const Driver = getDriver();

  const handleDownload = (): void => {
    downloadS3File(fileUrl).catch(() => addError('There was a problem downloading the file.'));
  };

  const downloadButton: ReactNode = (
    <IconButton aria-label="download" icon="zicon-download" onClick={handleDownload} title="Download file" variant="secondary" />
  );

  return (
    <div className="pg-viewer-wrapper">
      <div className="pg-viewer" id="pg-viewer" style={{ height: '70vh' }}>
        <ZoomElement additionalControlElement={downloadButton} zoomProps={{ wheel: { disabled: enableScrollY } }}>
          <Driver fileUrl={previewUrl} height={dimensions.height} width={dimensions.width} />
        </ZoomElement>
      </div>
    </div>
  );
};

export default FileViewer;

export type { Props as FileViewerProps };
